import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Divider, Tabs, Input, Button, Form, message } from 'antd';
import { useReactiveVar, useLazyQuery, useMutation } from '@apollo/client';

import { userVar, teamMembersVar, getUserId } from 'graphql/cache';
import {
  SearchIcon,
  FilterIcon,
  SortIcon,
  DoubleArrowIcon,
  UploadIcon,
  CloseIcon,
  PlusThinIcon,
  SliderIcon,
} from 'components/Icons';
import { preloadFont, addFontFaceRule } from 'helpers/proposal';
import { TEMPLATE_FILTER_ITEMS, COVERS_FILTER_ITEMS } from 'constants/index';
import { GET_CONFIGURATION } from 'graphql/queries/configurationQueries';
import { USER_UPDATE } from 'graphql/mutations/userMutations';
import {
  CONTENT_LIBRARY_ADD_CATALOG,
  CONTENT_LIBRARY_UPDATE_CATALOG,
} from 'graphql/mutations/contentLibraryMutations';
import { CatalogTab } from './components/Catalog';
import { GET_TREEVIEW_DATA } from 'graphql/queries/contentLibraryQueries';
import { ContentLibraryProvider } from 'context/ContentLibraryContext';
import { filterTreeData, handleTreeSearch, handleModalTreeSearch } from 'helpers/contentlibrary';
import { FETCH_TEAM_MEMBERS } from 'graphql/queries/usersTeamQueries';

import MultiSelect from 'components/MultiSelect';
import UploadContentLibraryItem from 'components/UploadContentLibraryItem/UploadContentLibraryItem';
import Loader from 'components/Loader';
import DynamicTooltip from 'components/DynamicTooltip';
import useWindowDimensions from 'hooks/useWindowDimensions';
import utils from 'utils/utils';
import PageHeader from 'components/PageHeader/PageHeader';
import TemplateTab from './components/TemplateTab/TemplateTab';
import CoverTab from './components/CoverTab/CoverTab';
import SectionTab from './components/SectionTab/SectionTab';
import MediaTab from './components/MediaTab/MediaTab';
import HeaderTab from './components/HeaderTab/HeaderTab';
import useTreeHandler from 'hooks/treeHandler';

import './ContentLibrary.scss';
import './components/ContentLibraryItem/ContentLibraryItem.scss';
import './components/ContentCard/ContentCard.scss';

const { TabPane } = Tabs;

/**
 * Content library
 *
 * @returns {React.ReactNode}
 */
const ContentLibrary = () => {
  const user = useReactiveVar(userVar);
  const teamMembers = useReactiveVar(teamMembersVar);
  const coversRef = useRef();
  const mediasRef = useRef();
  const inputRef = useRef(null);
  const catalogRef = useRef();
  const selectedTabRef = useRef('templates');
  const { width } = useWindowDimensions();
  const isMobile = width < 767;

  /**
   * Handle preload custom font
   *
   * @param {Object} user
   */
  const handlePreloadCustomFont = (user) => {
    const customFonts = user?.uploadedFonts || [];
    customFonts.forEach(({ family, url, weights, format }) => {
      preloadFont(url);
      addFontFaceRule(family, url, weights, format);
    });
  };

  /**
   * Get saved filter
   *
   * @returns {Array}
   */
  const getSavedFilter = () => {
    return !user?.templateFilter
      ? TEMPLATE_FILTER_ITEMS
      : TEMPLATE_FILTER_ITEMS.map((item) =>
          item.id === user.templateFilter ? { ...item, checked: true } : { ...item, checked: false }
        );
  };

  const [selectedTab, setSelectedTab] = useState('templates');
  const [activeKey, setActiveTab] = useState(utils.getQueryStringValue('tab') || '1');
  const [selectedCoverType, setCoverType] = useState('top');
  const [hideFolderMenu, setHideFolderMenu] = useState(true);
  const [config, setConfig] = useState({});
  const [showUploadModal, showOrHidesUploadModal] = useState(false);
  const showFilter = {
    templates: true,
    covers: true,
    headers: false,
    section: false,
    media: false,
  };
  const [searchValue, setSerachValue] = useState('');
  const [showSearchBox, showHideSearchBox] = useState(false);
  const [showAddNewProductPage, setShowAddNewProductPage] = useState(false);
  const [filterItems, setFilterItems] = useState(
    utils.getQueryStringValue('tab') === 'covers' ? COVERS_FILTER_ITEMS : getSavedFilter()
  );
  const [sortValue, setSortValue] = useState('desc');
  const [catalogThumbnails, setCatalogThumbnails] = useState([]);
  const [showImportCatalogModal, setShowImportCatalogModal] = useState(false);
  const [showCatalogSyncModal, setShowCatalogSyncModal] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [selectedFolderKey, changeSelectedFolderKey] = useState('templates');
  const [selectedFolderLevel, changeSelectedFolderLevel] = useState();
  const [selectedFolderName, changeSelectedFolderName] = useState();

  const [addProductForm] = Form.useForm();

  /**
   * Use tree handler
   */
  const {
    treeData,
    handleTreeData,
    setIndexes,
    initialTreeData,
    setInitialTreeData,
    setTreeData,
    addKey,
    getDefaultName,
  } = useTreeHandler();

  /**
   * Load config
   */
  const [loadConfig] = useLazyQuery(GET_CONFIGURATION, {
    fetchPolicy: 'cache-and-network',
    skip: config,
    onCompleted: (data) => {
      setConfig(data?.fetchConfiguration || {});
    },
  });

  const [loadTeamMembers] = useLazyQuery(FETCH_TEAM_MEMBERS, {
    variables: user?.roles?.includes('manager') ? { teamId: user?.teamId } : null,
    skip: !getUserId() || teamMembers.length,
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ fetchTeamMembers }) => {
      teamMembersVar(fetchTeamMembers);
    },
  });

  /**
   * Fetch tree view
   */
  const [fetchTreeView, { refetch: refetchTreeView, loading: loadingTreeView }] = useLazyQuery(
    GET_TREEVIEW_DATA,
    {
      fetchPolicy: 'network-only',
    }
  );

  /**
   * Handle refetch
   *
   */
  const handleRefetch = useCallback(
    async (keyToBeSelected, onKeySelect) => {
      fetchTreeView({
        variables: {
          type: selectedTabRef.current,
        },
        onCompleted: ({ fetchContentLibrary }) => {
          let tempTreeData = JSON.parse(JSON.stringify(fetchContentLibrary || {}));
          tempTreeData = setIndexes(tempTreeData);
          handleTreeData({
            data: JSON.parse(JSON.stringify(tempTreeData)) || {},
            type: selectedTabRef.current,
            keyToBeSelected,
            onKeySelect,
          });
          setInitialTreeData(JSON.parse(JSON.stringify(fetchContentLibrary)));
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchTreeView, handleTreeData, setIndexes, setInitialTreeData, selectedTabRef.current]
  );

  /**
   * Use effect
   */
  useEffect(() => {
    if (user && !teamMembers.length) {
      loadTeamMembers();
    }
    handleRefetch();
  }, [handleRefetch, loadTeamMembers, teamMembers.length, user]);

  /**
   * Save user
   */
  const [saveUser] = useMutation(USER_UPDATE, {
    async onCompleted({ updateUser }) {
      userVar(updateUser);
    },
  });

  /**
   * Add catalog
   */
  const [addCatalog] = useMutation(CONTENT_LIBRARY_ADD_CATALOG, {
    async onCompleted({ addCatalog }) {
      setShowAddNewProductPage(false);
      addProductForm.resetFields();
      setCatalogThumbnails([]);
      if (selectedTab === 'catalogs') {
        if (catalogRef && catalogRef.current) {
          await catalogRef.current.reloadItems();
        }
      }
      message.success('Product added successfully');
    },
  });

  /**
   * Update catalog
   */
  const [updateCatalog] = useMutation(CONTENT_LIBRARY_UPDATE_CATALOG, {
    async onCompleted({ updateCatalog }) {
      setShowAddNewProductPage(false);
      addProductForm.resetFields();
      setCatalogThumbnails([]);
      if (selectedTab === 'catalogs') {
        if (catalogRef && catalogRef.current) {
          await catalogRef.current.reloadItems();
        }
      }
      message.success('Product updated successfully');
      setSelectedCatalog(null);
    },
  });

  /**
   * Use effect
   */
  useEffect(() => {
    if (showSearchBox && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [showSearchBox]);

  /**
   * Use effect
   */
  useEffect(() => {
    onChangeTab(utils.getQueryStringValue('tab'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Use effect
   */
  useEffect(() => {
    if (
      !Object.keys(config)?.length &&
      (selectedTab === 'templates' || selectedTab === 'headers' || selectedTab === 'sections')
    ) {
      loadConfig({
        variables: {
          language: 'english',
        },
      });
    }
  }, [config, loadConfig, selectedTab]);

  /**
   * Use effect
   */
  useEffect(() => {
    handlePreloadCustomFont(user);
  }, [user]);

  /**
   * Use effect
   */
  useEffect(() => {
    document.title = 'Prospero - Content Library';
    document.body.classList.add('content-library-page');

    const style = document.createElement('style');
    style.id = 'library-scroll-lock';
    style.textContent = `
      html, body {
        overscroll-behavior: none;
        overflow: hidden;
        touch-action: none;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById('library-scroll-lock');
      if (existingStyle) {
        existingStyle.remove();
      }

      document.body.classList.remove('content-library-page');

      setTreeData({});
      setInitialTreeData({});
    };
  }, [setInitialTreeData, setTreeData]);

  /**
   * On change tab
   *
   * @param {string} key
   */
  const onChangeTab = (key) => {
    if (!key) {
      key = '1';
    }

    if (parseInt(key) === 1) {
      setFilterItems(getSavedFilter());
      setSelectedTab('templates');
      selectedTabRef.current = 'templates';
    } else if (parseInt(key) === 2) {
      setSelectedTab('covers');
      setFilterItems(COVERS_FILTER_ITEMS);
      selectedTabRef.current = 'covers';
    } else if (parseInt(key) === 3) {
      setSelectedTab('headers');
      selectedTabRef.current = 'headers';
    } else if (parseInt(key) === 4) {
      setSelectedTab('sections');
      selectedTabRef.current = 'sections';
    } else if (parseInt(key) === 5) {
      setSelectedTab('media');
      selectedTabRef.current = 'media';
    } else if (parseInt(key) === 6) {
      setSelectedTab('catalogs');
      selectedTabRef.current = 'catalogs';
    }

    setActiveTab(key);
    setHideFolderMenu(true);
    setShowAddNewProductPage(false);
    addProductForm.resetFields();
    setCatalogThumbnails([]);
    changeSelectedFolderKey(getDefaultName(selectedTabRef.current, 'key'));
  };

  /**
   * On change filter
   *
   * @param {Object} selectedItem
   */
  const onChangeFilter = (selectedItem) => {
    let updatedFilterList;
    if (selectedTab === 'covers') {
      updatedFilterList = filterItems.map((item) =>
        item.id === selectedItem.id
          ? { ...item, checked: !selectedItem.checked }
          : { ...item, checked: selectedItem.checked }
      );
      const { key } = updatedFilterList.find((item) => item.checked);
      setCoverType(key);
    } else {
      updatedFilterList = filterItems.map((item) =>
        item.id === selectedItem.id
          ? { ...item, checked: !selectedItem.checked }
          : { ...item, checked: false }
      );
    }

    setFilterItems(updatedFilterList);
    if (selectedTab === 'templates') {
      saveUser({
        variables: {
          user: {
            id: user._id,
            templateFilter: selectedItem.id,
          },
        },
      });
    }
  };

  /**
   * On change search
   *
   * @param {Object} e
   */
  const onChangeSearch = (e) => {
    setSerachValue(e.target.value);
  };

  /**
   * On search icon click
   */
  const onSearchIconClick = () => {
    showHideSearchBox(!showSearchBox);
    setSerachValue('');
  };

  /**
   * On clear filter
   *
   * @param {Object} e
   */
  const onClearFilter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChangeFilter({ id: 0, name: 'All', key: 'all', checked: false });
  };

  /**
   * Selected item
   */
  const selectedItem = filterItems?.find(
    (filterItem) => filterItem?.checked && filterItem?.key !== 'all'
  );

  /**
   * To title case
   *
   * @param {string} str
   */
  const toTitleCase = (str) => {
    return str?.replace(/\w\S*/g, function (txt) {
      return txt?.charAt(0)?.toUpperCase() + txt?.substr(1)?.toLowerCase();
    });
  };

  /**
   * On key select
   *
   * @param {Array} k
   */
  const onContentLibraryKeySelect = useCallback(
    (k) => {
      changeSelectedFolderKey(k?.[0]?.[0] || '');
      changeSelectedFolderLevel(k?.[0]?.[1] || '');
      changeSelectedFolderName(k?.[0]?.[2] || '');
      // if (String(k?.[0]?.[1]) === '4') {
      //   // max subfolder level allowed
      //   setSaveButtonDisabled(true);
      // } else {
      //   setSaveButtonDisabled(false);
      // }
    },
    [changeSelectedFolderKey, changeSelectedFolderLevel]
  );

  /**
   * Disabled filter
   */
  const disabledFilter =
    selectedTab === 'templates'
      ? filterItems?.find((item) => item.key === 'disabled')?.checked
      : false;

  /**
   * On upload click
   */
  const onUploadClick = () => {
    showOrHidesUploadModal(true);
  };

  /**
   * Cancel modal
   */
  const cancelModal = () => {
    showOrHidesUploadModal(false);
  };

  /**
   * Save product
   */
  const saveProduct = () => {
    addProductForm
      .validateFields()
      .then((values) => {
        const id = addProductForm.getFieldValue('_id');
        const discountType = addProductForm.getFieldValue('discountType');
        const folderKey = addProductForm.getFieldValue('folderKey') || selectedCatalog?.folderKey;

        // Extract price amount - handle both object and primitive cases
        let priceAmount = '0';
        if (values?.price !== undefined && values?.price !== null && values?.price !== '') {
          if (typeof values.price === 'object' && !Array.isArray(values.price)) {
            // Check if it's not an empty object and has amount property
            if (Object.keys(values.price).length > 0 && values.price.amount !== undefined) {
              priceAmount = values.price.amount;
            }
          } else {
            // Price is a primitive value
            priceAmount = values.price;
          }
        }

        const catalog = {
          name: values.name.trim(),
          description: values.description.trim(),
          price: {
            amount: String(priceAmount),
            type: values.paymentType,
            quantity: Number(values?.quantity || 1),
            items: Number(values?.items || 1),
          },
          discount: {
            value: values?.discount?.toString() || '0',
            unit: discountType,
          },
          images: catalogThumbnails.map((thumbnail) => ({
            name: thumbnail.name,
            url: thumbnail.url,
          })),
          folderKey: folderKey,
        };

        if (id) {
          updateCatalog({
            variables: {
              id,
              catalog: {
                ...catalog,
              },
            },
          });
        } else {
          addCatalog({
            variables: {
              catalog,
            },
          });
        }
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  /**
   * Check if user is loaded
   */
  if (!user) {
    return <Loader />;
  }

  /**
   * Is locked
   */
  const isLocked = user?.paymentStatus?.canLock || false;

  /**
   * Tab operations
   */
  const tabOperations = (
    <div className="tab-icons-container">
      <div className={`content-search-container ${showSearchBox ? 'showInput' : ''}`}>
        <Input
          ref={inputRef}
          className="content-search-input"
          placeholder={`Search ${toTitleCase(selectedTab)}...`}
          autoFocus
          value={searchValue}
          onChange={onChangeSearch}
          suffix={<CloseIcon onClick={onSearchIconClick} />}
        />
        <SearchIcon className="content-search-icon hoverable" onClick={onSearchIconClick} />
      </div>
      {showFilter[selectedTab] === true && (
        <React.Fragment>
          <Divider type="vertical" className="vertical-divider" />
          <MultiSelect
            filterList={filterItems}
            onChangeFilter={onChangeFilter}
            isRadio={true}
            showSelected
            iconComponent={
              selectedItem && selectedTab === 'templates' ? (
                <div className={`filter-icon hoverable ${selectedItem ? 'anticon-active' : ''}`}>
                  <FilterIcon />
                  <span className="text">{selectedItem?.name}</span>
                  <CloseIcon className="close-icon" onClick={onClearFilter} />
                </div>
              ) : (
                <FilterIcon className={`hoverable ${selectedItem ? 'anticon-active' : ''}`} />
              )
            }
          />
          <Divider type="vertical" className="vertical-divider" />
          <DynamicTooltip
            showTooltip={width > 1000}
            title={`Sort by entry date in ${
              sortValue === 'asc' ? 'descending' : 'ascending'
            } order`}>
            <SortIcon
              className="content-sort-icon hoverable"
              onClick={() => (sortValue === 'asc' ? setSortValue('desc') : setSortValue('asc'))}
            />
          </DynamicTooltip>
        </React.Fragment>
      )}

      {selectedTab === 'catalogs' && !showAddNewProductPage && (
        <span className="catalog-sync-btn" onClick={() => setShowCatalogSyncModal(true)}>
          <SliderIcon />
          Catalog sync
        </span>
      )}
    </div>
  );

  /**
   * Render
   */
  return (
    <div className="container container-content-library">
      <div className="content-library">
        <PageHeader
          pageTitle={
            <>
              <span className="above-md">Your</span> Content
            </>
          }
          showButton={
            showAddNewProductPage ? (
              <>
                <Button
                  className="large-btn add-catalog-btn"
                  onClick={() => {
                    setShowAddNewProductPage(false);
                    addProductForm.resetFields();
                    setCatalogThumbnails([]);
                    setSelectedCatalog(null);
                  }}
                  disabled={isLocked}>
                  CANCEL
                </Button>
                <Button
                  className="large-btn secondary-btn add-new-product-btn"
                  onClick={saveProduct}
                  disabled={isLocked}>
                  SAVE PRODUCT
                </Button>
              </>
            ) : (
              <>
                {isMobile && tabOperations}
                {(selectedTab === 'covers' || selectedTab === 'media') && (
                  <Button
                    className={`large-btn secondary-btn ${isMobile ? 'icon-only' : 'icon-right'}`}
                    onClick={onUploadClick}
                    disabled={isLocked}
                    icon={<UploadIcon className="upload-icon" />}>
                    {isMobile ? '' : 'UPLOAD'}
                  </Button>
                )}
                {selectedTab === 'catalogs' && (
                  <>
                    <Button
                      className={`large-btn add-catalog-btn ${
                        isMobile ? 'icon-only' : 'icon-right'
                      }`}
                      onClick={() => setShowImportCatalogModal(true)}
                      disabled={isLocked}>
                      {isMobile ? '' : 'ADD CATALOG'}
                      <PlusThinIcon className="add-catalog-icon" />
                    </Button>
                    <Button
                      className="large-btn secondary-btn add-new-product-btn"
                      onClick={() => setShowAddNewProductPage(true)}
                      disabled={isLocked}>
                      NEW PRODUCT
                    </Button>
                  </>
                )}
              </>
            )
          }
        />

        <ContentLibraryProvider
          treeData={treeData}
          setTreeData={setTreeData}
          handleTreeData={handleTreeData}
          setIndexes={setIndexes}
          initialTreeData={initialTreeData}
          setInitialTreeData={setInitialTreeData}
          handleRefetch={handleRefetch}
          fetchTreeView={fetchTreeView}
          refetchTreeView={refetchTreeView}
          loadingTreeView={loadingTreeView}
          handleModalTreeSearch={handleModalTreeSearch}
          handleTreeSearch={handleTreeSearch}
          filterTreeData={filterTreeData}
          addKey={addKey}
          selectedTabRef={selectedTabRef}
          selectedFolderKey={selectedFolderKey}
          selectedFolderLevel={selectedFolderLevel}
          selectedFolderName={selectedFolderName}
          changeSelectedFolderLevel={changeSelectedFolderLevel}
          changeSelectedFolderName={changeSelectedFolderName}
          onContentLibraryKeySelect={onContentLibraryKeySelect}
          changeSelectedFolderKey={(key) => {
            changeSelectedFolderKey(key);
            setHideFolderMenu(key !== null);
          }}
          getDefaultName={getDefaultName}>
          <Tabs
            activeKey={activeKey}
            tabBarExtraContent={!isMobile && tabOperations}
            onChange={onChangeTab}
            className={`content-library-tabs ${
              hideFolderMenu ? 'show-screen-2' : 'show-screen-1'
            }`}>
            <TabPane tab="Templates" key="1">
              <TemplateTab
                type="templates"
                teamMembers={teamMembers}
                filterItems={filterItems}
                disabledFilter={disabledFilter}
                searchValue={searchValue}
                sortValue={sortValue}
                user={user}
                deletedTemplates={user.deletedTemplates}
                saveUser={saveUser}
                config={config}
                loadConfig={loadConfig}
                isLocked={isLocked}
                tabKey={activeKey}
              />
            </TabPane>

            <TabPane tab="Covers" key="2">
              <CoverTab
                ref={coversRef}
                type="covers"
                filterItems={filterItems}
                autoExpandParent
                searchValue={searchValue}
                user={user}
                sortValue={sortValue}
                isLocked={isLocked}
              />
            </TabPane>

            <TabPane tab="Headers" key="3">
              <HeaderTab
                type="headers"
                filterItems={filterItems}
                searchValue={searchValue}
                user={user}
                config={config}
                loadConfig={loadConfig}
                isLocked={isLocked}
              />
            </TabPane>

            <TabPane tab="Sections" key="4">
              <SectionTab
                type="sections"
                filterItems={filterItems}
                searchValue={searchValue}
                user={user}
                config={config}
                setConfig={setConfig}
                loadConfig={loadConfig}
                isLocked={isLocked}
              />
            </TabPane>

            <TabPane tab="Media" key="5">
              <MediaTab
                ref={mediasRef}
                type="media"
                filterItems={filterItems}
                searchValue={searchValue}
                user={user}
                sortValue={sortValue}
                isLocked={isLocked}
              />
            </TabPane>

            <TabPane tab="Catalogs" key="6">
              <CatalogTab
                ref={catalogRef}
                type="catalogs"
                searchValue={searchValue}
                user={user}
                sortValue={sortValue}
                isLocked={isLocked}
                teamMembers={teamMembers}
                saveUser={saveUser}
                showAddNewProductPage={showAddNewProductPage}
                setShowAddNewProductPage={setShowAddNewProductPage}
                showImportCatalogModal={showImportCatalogModal}
                setShowImportCatalogModal={setShowImportCatalogModal}
                showCatalogSyncModal={showCatalogSyncModal}
                setShowCatalogSyncModal={setShowCatalogSyncModal}
                addProductForm={addProductForm}
                onUploadClick={onUploadClick}
                catalogThumbnails={catalogThumbnails}
                setCatalogThumbnails={setCatalogThumbnails}
                selectedCatalog={selectedCatalog}
                setSelectedCatalog={setSelectedCatalog}
              />
            </TabPane>

            {isMobile && (
              <button
                className="tab-menu-opener"
                onClick={() => setHideFolderMenu(!hideFolderMenu)}>
                <DoubleArrowIcon />
              </button>
            )}
          </Tabs>
        </ContentLibraryProvider>

        <UploadContentLibraryItem
          type={selectedTab}
          selectedFolderKey={selectedFolderKey}
          showUploadModal={showUploadModal}
          cancelUploadModal={cancelModal}
          openUploadModal={onUploadClick}
          selectedCoverType={selectedCoverType}
          onSuccess={async (urls) => {
            if (coversRef && coversRef.current) {
              await coversRef.current.reloadItems();
            }
            if (mediasRef && mediasRef.current) {
              await mediasRef.current.reloadItems();
            }
            if (selectedTab === 'catalogs') {
              setCatalogThumbnails([...catalogThumbnails, ...urls]);
            }
          }}
        />
      </div>
    </div>
  );
};

/**
 * Default props
 */
ContentLibrary.defaultProps = {
  templates: [],
  covers: [],
  sections: [],
  coversLibrary: [],
  config: null,
};

export default ContentLibrary;
